import React from 'react';
import { Link } from 'react-router-dom';
import { Card, StatsCard } from '../common/Card';
import { Heading, Text } from '../common/Typography';
import Button from '../common/Button';
import { getGenderTextColor } from '../../utils/genderColors';
import { SessionLadderToggle } from './SessionLadderToggle';
import SessionEloTrendChart from './SessionEloTrendChart';
import { formatOpponentNames } from '../../utils/sessionPlayerInsights';

const MOMENTUM_COPY = {
  strong_start_faded: 'Started strong and faded in the back half of the session.',
  slow_start_finished_strong: 'Started slow and finished strong — great closing form.',
  consistent: 'Rating held steady across the session — consistent performance.',
};

function formatElo(value) {
  return value != null ? Math.round(value) : '—';
}

function formatSignedInt(value) {
  if (value == null) return '—';
  const rounded = Math.round(value);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function formatSignedDecimal(value, decimals = 1) {
  if (value == null) return '—';
  const rounded = Number(value.toFixed(decimals));
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function formatWins(value) {
  return value != null ? value.toFixed(1) : '—';
}

function headlineTitle(record) {
  if (!record || record.wins + record.losses === 0) return 'Session summary';
  if (record.wins === 0) return 'On to the next one';
  if (record.wins > record.losses) return 'Great session!';
  if (record.wins < record.losses) return 'Tough one';
  return 'Solid effort';
}

/** Prefer the club-scoped Matchups tab (includes club-wide H2H); fall back to the all-clubs profile view. */
function buildMatchupsLink(opponentId, clubId) {
  return clubId
    ? `/clubs/${clubId}?tab=headtohead&opponentId=${opponentId}`
    : `/profile/matchups?opponentId=${opponentId}`;
}

const EXPECTED_WINS_INFO =
  'Expected wins is the sum of your win probability in each rated game, based on pre-game team ratings. Actual is how many games you won. The bar shows actual wins; the dashed line marks expected.';

function InsightInfoButton({ text, ariaLabel }) {
  if (!text) return null;
  return (
    <button
      type="button"
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-muted transition hover:bg-surface-subtle hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      title={text}
      aria-label={ariaLabel || text}
      onClick={(e) => e.stopPropagation()}
    >
      <svg
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    </button>
  );
}

function ExpectedActualBar({ expectedWins, actualWins, totalGames }) {
  if (!totalGames) return null;
  const actualPct = Math.min(100, Math.max(0, (actualWins / totalGames) * 100));
  const expectedPct =
    expectedWins != null ? Math.min(100, Math.max(0, (expectedWins / totalGames) * 100)) : null;

  return (
    <div
      className="relative h-2.5 w-full overflow-hidden rounded-full bg-surface-subtle"
      role="img"
      aria-label={
        expectedWins != null
          ? `Actual ${actualWins} wins, expected ${expectedWins.toFixed(1)} wins out of ${totalGames} games`
          : `Actual ${actualWins} wins out of ${totalGames} games`
      }
    >
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-success"
        style={{ width: `${actualPct}%` }}
      />
      {expectedPct != null && (
        <div
          className="pointer-events-none absolute inset-y-0 w-px border-l-2 border-dashed border-primary"
          style={{ left: `${expectedPct}%` }}
          aria-hidden
        />
      )}
    </div>
  );
}

function formatTeamEloMatchup(myTeamAvgElo, oppTeamAvgElo) {
  if (myTeamAvgElo == null && oppTeamAvgElo == null) return null;
  if (myTeamAvgElo != null && oppTeamAvgElo != null) {
    return `${formatElo(myTeamAvgElo)} vs ${formatElo(oppTeamAvgElo)}`;
  }
  if (myTeamAvgElo != null) return `Team ${formatElo(myTeamAvgElo)}`;
  return `Opp ${formatElo(oppTeamAvgElo)}`;
}

function HighlightTeamElo({ myTeamAvgElo, oppTeamAvgElo }) {
  const matchup = formatTeamEloMatchup(myTeamAvgElo, oppTeamAvgElo);
  if (!matchup) return null;
  return (
    <p className="text-xs tabular-nums text-text-muted" title="Team ratings at start of game">
      {matchup}
    </p>
  );
}

const HIGHLIGHT_ACCENTS = {
  success: 'border-success/25 bg-success-light',
  warning: 'border-warning/25 bg-warning-light',
  primary: 'border-primary/25 bg-primary-light',
  team2: 'border-team2-border/30 bg-team2-light',
};

function HighlightCard({ accent, label, children, empty }) {
  return (
    <div className={`rounded-2xl border p-3 ${HIGHLIGHT_ACCENTS[accent] || HIGHLIGHT_ACCENTS.primary}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">{label}</p>
      <div className="mt-1">
        {empty ? <p className="text-sm text-text-muted">Not enough data yet</p> : children}
      </div>
    </div>
  );
}

function RatingBreakdownRow({ label, breakdown }) {
  if (!breakdown) return null;
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-text-muted">{label}</span>
      <span className="font-semibold tabular-nums text-text-primary">
        {formatElo(breakdown.start)} → {formatElo(breakdown.end)}{' '}
        <span className={breakdown.change >= 0 ? 'text-success-dark' : 'text-danger-dark'}>
          ({formatSignedInt(breakdown.change)})
        </span>
      </span>
    </div>
  );
}

/**
 * Mobile-first "Summary" tab for the Session Performance page — a session
 * report: narrative headline, KPIs, match highlights, performance insights,
 * an enriched opponents list, and a session timeline strip.
 *
 * Pure presentation over `buildSessionPlayerInsights` output plus two lazily
 * loaded enrichments (season opponent-rating baseline, all-time H2H) — no
 * data fetching here. See SESSION_LIFECYCLE_ARCHITECTURE.md §3.7.
 */
export default function SessionSummaryTab({
  insights,
  ladderView,
  onLadderViewChange,
  canToggleLadder,
  mixedToggleLabel = 'Mixed',
  levelsToggleLabel = 'Levels',
  opponentRatingBaseline,
  opponentAllTimeRecords = {},
  opponentAllTimeLoading = false,
  opponentCurrentRatings = {},
  isSelf,
  subjectPlayerLabel = 'You',
  loading,
  clubId = null,
  hideRatingInsights = false,
}) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-40 animate-pulse rounded-2xl bg-surface-subtle" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl bg-surface-subtle" />
          ))}
        </div>
      </div>
    );
  }

  const ladderToggle = !hideRatingInsights && canToggleLadder && (
    <div className="flex justify-end">
      <SessionLadderToggle
        variant="light"
        ariaLabel="Summary ladder"
        tabs={[
          { id: 'all', label: 'All' },
          { id: 'mixed', label: mixedToggleLabel },
          { id: 'levels', label: levelsToggleLabel },
        ]}
        value={ladderView}
        onChange={onLadderViewChange}
      />
    </div>
  );

  if (!insights?.hasGames) {
    return (
      <div className="space-y-4">
        {ladderToggle}
        <div className="py-8 text-center text-text-muted">
          {isSelf ? 'You' : subjectPlayerLabel} didn&apos;t play any games this session.
        </div>
      </div>
    );
  }

  const {
    record,
    scoring,
    highlights,
    facts,
    ratingBreakdown,
    opponentSessionRecords,
    avgOpponentTeamRating,
    eloSeries,
    mixedEloSeries,
    levelsEloSeries,
    momentumLabel,
    narrativeSummary,
    gamesPlayed,
    gameTimeline,
  } = insights;

  const momentumText = momentumLabel ? MOMENTUM_COPY[momentumLabel] : null;

  // Headline rating figure — a single ladder's start/end/change, or (in 'all'
  // with both ladders played) just a combined delta, since two rating pools
  // can't share one "start -> end" number.
  const mixedBreakdown = ratingBreakdown?.mixed;
  const levelsBreakdown = ratingBreakdown?.levels;
  let headlineStart = null;
  let headlineEnd = null;
  let headlineChange = null;
  if (ladderView === 'mixed') {
    headlineStart = mixedBreakdown?.start;
    headlineEnd = mixedBreakdown?.end;
    headlineChange = mixedBreakdown?.change;
  } else if (ladderView === 'levels') {
    headlineStart = levelsBreakdown?.start;
    headlineEnd = levelsBreakdown?.end;
    headlineChange = levelsBreakdown?.change;
  } else if (mixedBreakdown && levelsBreakdown) {
    headlineChange = (mixedBreakdown.change ?? 0) + (levelsBreakdown.change ?? 0);
  } else if (mixedBreakdown) {
    headlineStart = mixedBreakdown.start;
    headlineEnd = mixedBreakdown.end;
    headlineChange = mixedBreakdown.change;
  } else if (levelsBreakdown) {
    headlineStart = levelsBreakdown.start;
    headlineEnd = levelsBreakdown.end;
    headlineChange = levelsBreakdown.change;
  }

  const toughestOpponentGap =
    facts.toughestOpponentBeat?.oppTeamAvgElo != null && facts.toughestOpponentBeat?.myTeamAvgElo != null
      ? Math.round(facts.toughestOpponentBeat.oppTeamAvgElo - facts.toughestOpponentBeat.myTeamAvgElo)
      : null;

  const showBothRatingRows = Boolean(mixedBreakdown && levelsBreakdown);

  return (
    <div className="space-y-5">
      {ladderToggle}

      {/* Headline + rating trend */}
      <Card variant="default" size="small" className="!p-4 sm:!p-5">
        <div className={`flex flex-col gap-4 ${hideRatingInsights ? '' : 'sm:flex-row sm:items-stretch sm:justify-between sm:gap-6'}`}>
          <div className={hideRatingInsights ? '' : 'sm:max-w-[52%]'}>
            <div className="mb-1 flex items-center gap-1.5">
              <span aria-hidden>✨</span>
              <Heading level={3} className="text-base text-text-primary">
                {headlineTitle(record)}
              </Heading>
            </div>
            {narrativeSummary && <Text className="text-sm text-text-secondary">{narrativeSummary}</Text>}
            {!hideRatingInsights && momentumText && <Text className="mt-2 text-xs text-text-muted">{momentumText}</Text>}
          </div>
          {!hideRatingInsights ? (
          <div className="flex-1 sm:min-w-[45%]">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              Rating this session
            </p>
            <div className="flex items-baseline gap-2">
              <span
                className={`text-2xl font-bold ${
                  headlineChange > 0 ? 'text-success-dark' : headlineChange < 0 ? 'text-danger-dark' : 'text-text-primary'
                }`}
              >
                {formatSignedInt(headlineChange)}
              </span>
              {headlineStart != null && headlineEnd != null && (
                <span className="text-xs text-text-muted">
                  {formatElo(headlineStart)} → {formatElo(headlineEnd)}
                </span>
              )}
            </div>
            <div className="mt-2 h-32 sm:h-36">
              <SessionEloTrendChart
                series={eloSeries}
                dualSeries={{ mixed: mixedEloSeries, levels: levelsEloSeries }}
                ladderView={ladderView}
              />
            </div>
          </div>
          ) : null}
        </div>
      </Card>

      {/* Scoring KPIs */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatsCard
          dense
          title="Win / loss"
          value={`${record.wins}-${record.losses}`}
          description={record.winPct != null ? `${Math.round(record.winPct)}% win rate` : null}
          valueClassName={record.wins > record.losses ? '!text-success' : record.losses > record.wins ? '!text-danger' : ''}
        />
        <StatsCard
          dense
          title="Avg point diff"
          value={formatSignedDecimal(scoring.avgPointDiff)}
          valueClassName={
            scoring.avgPointDiff > 0 ? '!text-success' : scoring.avgPointDiff < 0 ? '!text-danger' : ''
          }
        />
        <StatsCard dense title="Points for" value={scoring.pointsFor} />
        <StatsCard dense title="Points against" value={scoring.pointsAgainst} />
      </div>

      {/* Match highlights */}
      <div>
        <Heading level={3} className="mb-2 text-base text-text-primary">
          Match highlights
        </Heading>
        <div className={`grid grid-cols-1 gap-2 ${hideRatingInsights ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
          <HighlightCard accent="success" label="Biggest win" empty={!highlights.biggestWin}>
            {highlights.biggestWin && (
              <>
                <p className="text-sm font-semibold text-success-dark">
                  {highlights.biggestWin.myScore}-{highlights.biggestWin.oppScore}
                </p>
                <p className="text-xs text-text-muted">vs {formatOpponentNames(highlights.biggestWin.opponents)}</p>
                <HighlightTeamElo
                  myTeamAvgElo={hideRatingInsights ? null : highlights.biggestWin.myTeamAvgElo}
                  oppTeamAvgElo={hideRatingInsights ? null : highlights.biggestWin.oppTeamAvgElo}
                />
              </>
            )}
          </HighlightCard>
          <HighlightCard accent="warning" label="Toughest match" empty={!highlights.toughestMatch}>
            {highlights.toughestMatch && (
              <>
                <p className="text-sm font-semibold text-text-primary">
                  {highlights.toughestMatch.myScore}-{highlights.toughestMatch.oppScore}
                </p>
                <p className="text-xs text-text-muted">vs {formatOpponentNames(highlights.toughestMatch.opponents)}</p>
                <HighlightTeamElo
                  myTeamAvgElo={hideRatingInsights ? null : highlights.toughestMatch.myTeamAvgElo}
                  oppTeamAvgElo={hideRatingInsights ? null : highlights.toughestMatch.oppTeamAvgElo}
                />
              </>
            )}
          </HighlightCard>
          {!hideRatingInsights ? (
            <>
              <HighlightCard accent="primary" label="Biggest rating gain" empty={!facts.biggestRatingGain}>
                {facts.biggestRatingGain && (
                  <>
                    <p className="text-sm font-semibold text-primary">{formatSignedInt(facts.biggestRatingGain.eloChange)}</p>
                    <p className="text-xs text-text-muted">vs {formatOpponentNames(facts.biggestRatingGain.opponents)}</p>
                    <HighlightTeamElo
                      myTeamAvgElo={facts.biggestRatingGain.myTeamAvgElo}
                      oppTeamAvgElo={facts.biggestRatingGain.oppTeamAvgElo}
                    />
                  </>
                )}
              </HighlightCard>
              <HighlightCard accent="team2" label="Toughest opponent beat" empty={!facts.toughestOpponentBeat}>
                {facts.toughestOpponentBeat && (
                  <>
                    <p className="text-sm font-semibold text-text-primary">
                      {formatOpponentNames(facts.toughestOpponentBeat.opponents)}
                    </p>
                    <HighlightTeamElo
                      myTeamAvgElo={facts.toughestOpponentBeat.myTeamAvgElo}
                      oppTeamAvgElo={facts.toughestOpponentBeat.oppTeamAvgElo}
                    />
                    {toughestOpponentGap != null && toughestOpponentGap > 0 && (
                      <p className="text-xs text-text-muted">Beat opponent rated {toughestOpponentGap} points higher</p>
                    )}
                  </>
                )}
              </HighlightCard>
            </>
          ) : null}
        </div>
      </div>

      {/* Performance insights */}
      {!hideRatingInsights ? (
      <div>
        <Heading level={3} className="mb-2 text-base text-text-primary">
          Performance insights
        </Heading>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-xl border border-border-light bg-surface-subtle/60 px-3 py-2.5">
            <div className="flex items-center gap-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Expected vs actual</p>
              <InsightInfoButton
                text={EXPECTED_WINS_INFO}
                ariaLabel="How expected vs actual wins works"
              />
            </div>
            <div className="mt-2">
              <div className="mb-1.5 flex items-center justify-between text-xs text-text-muted">
                <span>
                  Actual{' '}
                  <span className="font-semibold tabular-nums text-text-primary">{facts.actualWins}</span>
                  {facts.expectedWins != null && (
                    <>
                      {' '}
                      · Expected{' '}
                      <span className="font-semibold tabular-nums text-text-primary">
                        {formatWins(facts.expectedWins)}
                      </span>
                    </>
                  )}
                </span>
                <span className="tabular-nums">{gamesPlayed} games</span>
              </div>
              <ExpectedActualBar
                expectedWins={facts.expectedWins}
                actualWins={facts.actualWins}
                totalGames={gamesPlayed}
              />
            </div>
            {facts.overperformance != null && (
              <p
                className={`mt-2 text-xs font-medium ${
                  facts.overperformance >= 0 ? 'text-success-dark' : 'text-danger-dark'
                }`}
              >
                {facts.overperformance >= 0 ? 'Won' : 'Lost'}{' '}
                {Math.abs(facts.overperformance).toFixed(1)} more than expected
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border-light bg-surface-subtle/60 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Average opponent rating</p>
            <p className="mt-1 text-xl font-bold text-text-primary">{formatElo(avgOpponentTeamRating)}</p>
            {opponentRatingBaseline?.average != null && avgOpponentTeamRating != null ? (
              <p className="text-xs text-text-muted">
                {formatSignedInt(Math.round(avgOpponentTeamRating - opponentRatingBaseline.average))} vs your season
                average ({formatElo(opponentRatingBaseline.average)})
              </p>
            ) : (
              <p className="text-xs text-text-muted">Season comparison not available yet</p>
            )}
          </div>

          <div className="rounded-xl border border-border-light bg-surface-subtle/60 px-3 py-2.5">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Rating breakdown</p>
            {mixedBreakdown || levelsBreakdown ? (
              <div className="space-y-1.5">
                <RatingBreakdownRow label={showBothRatingRows ? 'Mixed' : 'Start → End'} breakdown={mixedBreakdown} />
                <RatingBreakdownRow label={showBothRatingRows ? 'Levels' : 'Start → End'} breakdown={levelsBreakdown} />
              </div>
            ) : (
              <p className="text-sm text-text-muted">Not enough rated games yet</p>
            )}
          </div>
        </div>
      </div>
      ) : null}

      {/* Opponents faced this session */}
      {opponentSessionRecords.length > 0 && (
        <div>
          <Heading level={3} className="mb-2 text-base text-text-primary">
            Opponents this session
          </Heading>
          <div className="divide-y divide-border-light rounded-xl border border-border-light bg-surface">
            {opponentSessionRecords.map((opp) => {
              const allTime = opponentAllTimeRecords[opp.opponentId];
              const ratingRow = opponentCurrentRatings[opp.opponentId];
              const currentRating = ratingRow
                ? (opp.lastPairType === 'levels' ? ratingRow.levels : ratingRow.mixed)
                : null;
              return (
                <div key={opp.opponentId} className="flex flex-col gap-1 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`truncate text-sm font-medium ${getGenderTextColor(opp.gender)}`}>{opp.name}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      {opponentAllTimeLoading ? (
                        <span className="h-3 w-14 animate-pulse rounded bg-surface-subtle" />
                      ) : allTime ? (
                        <span className="text-xs font-semibold tabular-nums text-text-secondary">
                          {allTime.wins}-{allTime.losses} all-time
                        </span>
                      ) : null}
                      {isSelf && (
                        <Button
                          as={Link}
                          to={buildMatchupsLink(opp.opponentId, clubId)}
                          variant="outline"
                          size="small"
                          className="!min-h-0 !px-2.5 !py-1 text-xs"
                        >
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
                    <span>
                      Session {opp.wins}W-{opp.losses}L
                    </span>
                    {currentRating != null && !hideRatingInsights && <span>Rated {formatElo(currentRating)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Session timeline */}
      {gameTimeline.length > 0 && (
        <div>
          <Heading level={3} className="mb-2 text-base text-text-primary">
            Session timeline
          </Heading>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {gameTimeline.map((g) => (
              <div
                key={g.gameId}
                className="min-w-[8.5rem] shrink-0 rounded-xl border border-border-light bg-surface-subtle/60 px-3 py-2.5"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">Game {g.gameNumber}</p>
                <p className={`mt-0.5 text-sm font-semibold ${g.result === 'win' ? 'text-success-dark' : 'text-danger-dark'}`}>
                  {g.myScore}-{g.oppScore}
                </p>
                <p className="truncate text-xs text-text-muted">vs {formatOpponentNames(g.opponents)}</p>
                {g.eloChange != null && !hideRatingInsights && (
                  <p
                    className={`mt-1 text-xs font-semibold tabular-nums ${
                      g.eloChange > 0 ? 'text-success-dark' : g.eloChange < 0 ? 'text-danger-dark' : 'text-text-muted'
                    }`}
                  >
                    {formatSignedInt(g.eloChange)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
